// src/pages/PublicLinkInBioPage.tsx

import { useEffect, useState } from 'react';
import { getPublicPage, logPageEvent, submitEmailSignup } from '@/api/linkInBioPublic';
import type { PublicPageData } from '@/types/linkInBio';
import LinkInBioRenderer from '@/components/kit/link-in-bio/LinkInBioRenderer';
import ReportModal from '@/components/kit/link-in-bio/ReportModal';

interface PublicLinkInBioPageProps {
  slug: string;
}

export default function PublicLinkInBioPage({ slug }: PublicLinkInBioPageProps) {
  const [pageData, setPageData] = useState<PublicPageData | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!slug) return;

    // Extract tracking ID from query string
    const params = new URLSearchParams(window.location.search);
    const trackingId = params.get('id') || undefined;

    const fetchPage = async () => {
      try {
        const data = await getPublicPage(slug);
        setPageData(data);

        // Log page view
        logPageEvent(slug, {
          event_type: 'page_view',
          referrer: document.referrer || undefined,
          tracking_id: trackingId,
        }).catch(() => {}); // Fire and forget
      } catch (err: any) {
        setError(err?.error || { code: 'NOT_FOUND', message: 'Page not found' });
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  // Set the browser tab title to the artist's display name
  useEffect(() => {
    if (pageData?.display_name) {
      document.title = pageData.display_name;
    } else if (error) {
      document.title = error.code === 'SUSPENDED' ? 'Page Not Available' : 'Page Not Found';
    }
  }, [pageData, error]);

  const handleLinkClick = (linkId: string, label: string) => {
    if (!slug) return;
    logPageEvent(slug, {
      event_type: 'link_click',
      link_id: linkId,
      link_label: label,
    }).catch(() => {});
  };

  const handleEmailSubmit = async (email: string) => {
    if (!slug) return;
    await submitEmailSignup(slug, email);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-white" />
      </div>
    );
  }

  // Error states
  if (error) {
    if (error.code === 'SUSPENDED') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
          <svg className="mb-4 h-16 w-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h1 className="text-xl font-bold text-white">Page Not Available</h1>
          <p className="mt-2 text-sm text-gray-400">
            This page has been removed for violating our terms of service.
          </p>
          <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">
            Powered by BandJerry
          </a>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
        <svg className="mb-4 h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <h1 className="text-xl font-bold text-white">Page Not Found</h1>
        <p className="mt-2 text-sm text-gray-400">
          This page doesn't exist or isn't available right now.
        </p>
        <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">
          Create your own — Powered by BandJerry
        </a>
      </div>
    );
  }

  if (!pageData) return null;

  return (
    <div className="min-h-screen">
      <LinkInBioRenderer
        data={pageData}
        onLinkClick={handleLinkClick}
        onEmailSubmit={handleEmailSubmit}
      />

      {/* Report button - fixed at bottom */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={() => setShowReport(true)}
          className="rounded-full bg-black/30 px-3 py-1.5 text-xs text-white/50 backdrop-blur-sm transition-colors hover:text-white/80"
        >
          Report
        </button>
      </div>

      {showReport && slug && (
        <ReportModal slug={slug} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
